import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import withDragAndDrop, {
  type EventInteractionArgs,
} from 'react-big-calendar/lib/addons/dragAndDrop';
import { useAppointments } from '@features/appointments';
import { Box, Typography } from '@mui/material';
import { format, getDay, parse, startOfWeek } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';

import {
  mergeAppointmentOverride,
  useAppointmentOverrides,
} from '../../context/AppointmentOverridesContext';
import { useExperimentalCalendarFilters } from '../../context/ExperimentalCalendarFilters';
import { type ExperimentalAppointment, getFixtureProviders, type Provider } from '../../fixtures';
import { getExtendedColor, providerInitials } from '../../theme/extendedPalette';

import CalendarContainer from './components/CalendarContainer';
import CalendarLoadingOverlay from './components/CalendarLoadingOverlay';
import {
  CustomHeader,
  CustomMonthHeader,
  CustomTimeGutterHeader,
} from './components/CustomHeaders';
import { EventComponent } from './components/EventComponent';
import { type CalendarEvent } from './types';
import {
  DEFAULT_ENCOUNTER_DURATION,
  getAppointmentDisplayTitle,
  getBigCalendarView,
  VALID_STEPS,
  VALID_TIMESLOTS,
} from './utils';

import '../../styles/calendar-custom.scss';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: { 'en-US': enUS },
});

// Drag-and-drop enabled calendar. Production wires d&d via the same HOC; we
// route updates through the override context instead of an RTK mutation.
const DnDCalendar = withDragAndDrop<CalendarEvent>(Calendar);

// Resource-mode column header — provider initials badge + name + title.
// RBC passes the full resource object via the `resource` prop.
const ResourceHeader = ({ resource }: { resource: unknown }) => {
  const provider = resource as Provider;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1,
        py: 0.75,
        minWidth: 0,
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 18,
          minWidth: 24,
          px: 0.5,
          borderRadius: '4px',
          bgcolor: getExtendedColor(provider.color),
          color: '#fff',
          fontSize: 10,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {providerInitials(provider.name)}
      </Box>
      <Box sx={{ minWidth: 0, textAlign: 'left' }}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {provider.name}
        </Typography>
        {provider.title && (
          <Typography sx={{ fontSize: 10, opacity: 0.7, lineHeight: 1.2 }}>
            {provider.title}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const CalendarWidget: React.FC = () => {
  const {
    appointments: rawAppointments,
    isLoading,
    isBackgroundLoading,
    viewMode,
    setViewMode,
    currentDate,
    calendarGranularity,
  } = useAppointments();

  // Fixtures are guaranteed to carry block_state — see fixtures/index.ts.
  // The production AppointmentResponse type doesn't yet include block_state, so
  // narrow at the read boundary.
  const allAppointments = rawAppointments as ExperimentalAppointment[];

  const { getOverride, setOverride } = useAppointmentOverrides();
  const { isProviderSelected, showUnassigned, selectedProviderIds } =
    useExperimentalCalendarFilters();

  const appointments = useMemo(
    () =>
      allAppointments.filter((a) => {
        if (a.provider_id == null) return showUnassigned;
        return isProviderSelected(a.provider_id);
      }),
    [allAppointments, isProviderSelected, showUnassigned],
  );

  // Resource-mode: split day view into per-provider columns when >=2 providers
  // are selected. RBC drops events whose resourceAccessor doesn't match any
  // resource ID, so unassigned events are filtered out in this mode.
  const showResourceMode = viewMode === 'day' && selectedProviderIds.size >= 2;
  const resources = useMemo<Provider[] | undefined>(() => {
    if (!showResourceMode) return undefined;
    return getFixtureProviders().filter((p) => selectedProviderIds.has(p.provider_id));
  }, [showResourceMode, selectedProviderIds]);

  const [events, setEvents] = useState<CalendarEvent[]>([]);

  const safeStep = useMemo(() => {
    const step = calendarGranularity?.step;
    return VALID_STEPS.includes(step) ? step : 15;
  }, [calendarGranularity?.step]);

  const safeTimeslots = useMemo(() => {
    const timeslots = calendarGranularity?.timeslots;
    return VALID_TIMESLOTS.includes(timeslots) ? timeslots : 4;
  }, [calendarGranularity?.timeslots]);

  const scrollToTime = useMemo(() => {
    const t = new Date();
    t.setHours(t.getHours() - 2, 0, 0);
    return t;
  }, []);

  useEffect(() => {
    const calendarEvents = appointments.map((appointment) => {
      // Fold overrides in so drag/resize moves and the drawer's edits both
      // reflect on the grid layout — not just inside the per-event card.
      const merged = mergeAppointmentOverride(
        appointment,
        getOverride(appointment.appointment_id),
      );
      const startTime = new Date(merged.start_time);
      const endTime = new Date(startTime);
      const durationMinutes = merged.duration || DEFAULT_ENCOUNTER_DURATION;
      endTime.setMinutes(endTime.getMinutes() + durationMinutes);

      return {
        id: merged.appointment_id,
        title: getAppointmentDisplayTitle(merged),
        start: startTime,
        end: endTime,
        resource: merged,
      };
    });

    setEvents(calendarEvents);
  }, [appointments, getOverride]);

  // Drag to reschedule — RBC's drop handler hands us new start/end Dates and,
  // in resource-mode, a resourceId for the column the event was dropped into.
  // We translate that into an override (start_time + duration, plus
  // provider_id when columns change). useMergedAppointment downstream and
  // the events useEffect above pick the change up automatically.
  //
  // We also patch the local events array synchronously inside the handler.
  // Without this RBC briefly renders the event back at its original
  // position between when the override commits and when the events
  // useEffect re-derives.
  const handleEventDrop = useCallback(
    ({ event, start, end, resourceId }: EventInteractionArgs<CalendarEvent>) => {
      const startDate = start instanceof Date ? start : new Date(start);
      const endDate = end instanceof Date ? end : new Date(end);
      const durationMin = Math.max(
        1,
        Math.round((endDate.getTime() - startDate.getTime()) / 60_000),
      );
      const partial: Parameters<typeof setOverride>[1] = {
        start_time: startDate.toISOString(),
        duration: durationMin,
      };
      if (typeof resourceId === 'string') {
        partial.provider_id = resourceId;
      }
      setEvents((prev) =>
        prev.map((e) => (e.id === event.id ? { ...e, start: startDate, end: endDate } : e)),
      );
      setOverride(event.id, partial);
    },
    [setOverride],
  );

  // Resize: RBC reports new start AND end (top-edge drag moves start, bottom
  // moves end). Writing both keeps the override consistent regardless of
  // which handle the user grabbed.
  const handleEventResize = useCallback(
    ({ event, start, end }: EventInteractionArgs<CalendarEvent>) => {
      const startDate = start instanceof Date ? start : new Date(start);
      const endDate = end instanceof Date ? end : new Date(end);
      const durationMin = Math.max(
        1,
        Math.round((endDate.getTime() - startDate.getTime()) / 60_000),
      );
      setEvents((prev) =>
        prev.map((e) => (e.id === event.id ? { ...e, start: startDate, end: endDate } : e)),
      );
      setOverride(event.id, {
        start_time: startDate.toISOString(),
        duration: durationMin,
      });
    },
    [setOverride],
  );

  const EventComponentWrapper = useCallback(
    ({ event, title }: { event: CalendarEvent; title: React.ReactNode }) => {
      return <EventComponent event={event} title={title} viewMode={viewMode} />;
    },
    [viewMode],
  );

  return (
    <CalendarContainer>
      <CalendarLoadingOverlay isLoading={isLoading} isBackgroundLoading={isBackgroundLoading} />

      <DnDCalendar
        className={resources ? 'resource-mode' : undefined}
        localizer={localizer}
        events={events}
        defaultView={getBigCalendarView(viewMode)}
        view={getBigCalendarView(viewMode)}
        views={[Views.DAY, Views.WEEK, Views.MONTH]}
        step={safeStep}
        showMultiDayTimes
        date={currentDate}
        onView={setViewMode}
        scrollToTime={scrollToTime}
        selectable={false}
        tooltipAccessor={null}
        style={{ height: '100%' }}
        timeslots={safeTimeslots}
        // "overlap" stacks colliding events with a horizontal offset (Google
        // Calendar / Cron style) instead of splitting the column into thin
        // vertical lanes. Each event keeps near-full width; the stacking
        // order is z-index based so hover (which bumps to z-index 10 on
        // EventWrapper) brings the focused one to the front.
        dayLayoutAlgorithm="overlap"
        toolbar={false}
        resources={resources}
        resourceIdAccessor={resources ? ((p: object) => (p as Provider).provider_id) : undefined}
        resourceTitleAccessor={
          resources
            ? ((p: object) => {
                const provider = p as Provider;
                return provider.title ? `${provider.name}, ${provider.title}` : provider.name;
              })
            : undefined
        }
        resourceAccessor={
          resources ? ((event) => (event).resource.provider_id) : undefined
        }
        onEventDrop={handleEventDrop}
        onEventResize={handleEventResize}
        resizable
        components={{
          event: EventComponentWrapper,
          week: { header: CustomHeader },
          month: { header: CustomMonthHeader },
          ...(resources && { resourceHeader: ResourceHeader }),
          ...(viewMode === 'day' && !resources && {
            timeGutterHeader: () => <CustomTimeGutterHeader currentDate={currentDate} />,
          }),
        }}
      />
    </CalendarContainer>
  );
};

export default CalendarWidget;
